import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: var(--section-max-width);
  margin: 0 auto;

  .projects-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 20px;
    position: relative;
    margin-top: 50px;
    width: 100%;
  }
`;

const StyledProject = styled.li`
  position: relative;
  cursor: default;
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .project-inner {
        transform: translateY(-7px);
      }
    }
  }

  .project-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--surface-raised);
    transition: var(--transition);
    overflow: auto;
  }

  .project-title {
    margin: 0 0 12px;
    color: var(--text-bright);
    font-size: var(--fz-xxl);
    font-weight: 600;
  }

  .project-description {
    color: var(--text-muted);
    font-size: clamp(var(--fz-md), 1.35vw, 17px);

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .project-tech-list {
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
    flex-wrap: wrap;
    padding: 0;
    margin: 20px 0 0 0;
    list-style: none;

    li {
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      line-height: 1.75;

      &:not(:last-of-type) {
        margin-right: 15px;
      }
    }
  }
`;

const Projects = () => {
  const data = useStaticQuery(graphql`
    query {
      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
            }
            html
          }
        }
      }
    }
  `);

  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion || !sr) {
      return undefined;
    }

    const titleEl = revealTitle.current;
    if (titleEl) {
      sr.reveal(titleEl, srConfig());
    }
    revealProjects.current.forEach((ref, i) => {
      if (ref) {
        sr.reveal(ref, srConfig(i * 100));
      }
    });

    return () => {
      if (!sr) {
        return;
      }
      if (titleEl) {
        sr.clean(titleEl);
      }
      revealProjects.current.forEach(ref => {
        if (ref) {
          sr.clean(ref);
        }
      });
    };
  }, [prefersReducedMotion]);

  const projects = data.projects.edges.filter(({ node }) => node);

  const projectInner = node => {
    const { frontmatter, html } = node;
    const { title, tech } = frontmatter;

    return (
      <div className="project-inner">
        <header>
          <h3 className="project-title">{title}</h3>

          <div className="project-description" dangerouslySetInnerHTML={{ __html: html }} />
        </header>

        <footer>
          {tech && (
            <ul className="project-tech-list">
              {tech.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </footer>
      </div>
    );
  };

  return (
    <StyledProjectsSection id="projects">
      <h2 ref={revealTitle} className="numbered-heading">
        Some Things I’ve Built
      </h2>

      <ul className="projects-grid">
        {projects.map(({ node }, i) => (
          <StyledProject key={i} ref={el => (revealProjects.current[i] = el)}>
            {projectInner(node)}
          </StyledProject>
        ))}
      </ul>
    </StyledProjectsSection>
  );
};

export default Projects;
